import { useQuery } from '@tanstack/react-query'
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Package,
  Calendar,
  BarChart3,
  Percent,
  ShoppingCart,
  RefreshCw,
  AlertCircle,
  Users
} from 'lucide-react'
import { productStatsApi, ProductStats, SalesYearItem, PriceHistoryItem, SalesMonthItem, SalesByClientTypeItem } from '@/lib/api'
import { cn } from '@/lib/utils'

interface ProductStatsPanelProps {
  reference: string
  className?: string
}

export default function ProductStatsPanel({ reference, className }: ProductStatsPanelProps) {
  const { data: stats, isLoading, error, refetch } = useQuery({
    queryKey: ['product-stats', reference],
    queryFn: () => productStatsApi.getProductStats(reference),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false
  })

  const formatCurrency = (value: number | null) => {
    if (value === null || value === undefined) return '-'
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(value)
  }

  const formatPercent = (value: number | null) => {
    if (value === null || value === undefined) return '-'
    return `${value.toFixed(1)}%`
  }

  const formatNumber = (value: number | null) => {
    if (value === null || value === undefined) return '-'
    return new Intl.NumberFormat('es-ES').format(value)
  }

  if (isLoading) {
    return (
      <div className={cn("bg-white rounded-lg border shadow-sm p-6", className)}>
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
          <span className="ml-2 text-slate-500">Cargando estadisticas...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={cn("bg-white rounded-lg border shadow-sm p-6", className)}>
        <div className="flex items-center justify-center py-8 text-slate-500">
          <AlertCircle className="h-5 w-5 mr-2" />
          <span>No hay estadisticas disponibles para este producto</span>
          <button
            onClick={() => refetch()}
            className="ml-4 text-primary-600 hover:text-blue-700 text-sm"
          >
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  if (!stats || (!stats.price_history.length && !stats.sales_by_year.length)) {
    return (
      <div className={cn("bg-white rounded-lg border shadow-sm p-6", className)}>
        <div className="flex items-center justify-center py-8 text-slate-500">
          <BarChart3 className="h-5 w-5 mr-2" />
          <span>Sin datos historicos para este producto</span>
        </div>
      </div>
    )
  }

  // Get current year data
  const currentYear = new Date().getFullYear().toString()
  const currentYearSales = stats.sales_by_year.find(s => s.ejercicio === currentYear)
  const currentYearPrices = stats.price_history.find(p => p.ejercicio === currentYear)

  // Calculate year over year changes
  const previousYear = (parseInt(currentYear) - 1).toString()
  const previousYearSales = stats.sales_by_year.find(s => s.ejercicio === previousYear)

  let salesChange: number | null = null
  if (currentYearSales && previousYearSales && previousYearSales.total_revenue > 0) {
    salesChange = ((currentYearSales.total_revenue - previousYearSales.total_revenue) / previousYearSales.total_revenue) * 100
  }

  return (
    <div className={cn("bg-white rounded-lg border shadow-sm", className)}>
      {/* Header */}
      <div className="px-6 py-4 border-b">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary-600" />
            Estadisticas Factusol
          </h3>
          <span className="text-sm text-slate-500">Ref: {reference}</span>
        </div>
      </div>

      {/* Summary Cards */}
      {stats.summary && stats.summary.total_units_sold > 0 && (
        <div className="px-6 py-4 grid grid-cols-2 md:grid-cols-4 gap-4 border-b bg-slate-50">
          <div className="text-center">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Unidades Vendidas</p>
            <p className="text-2xl font-bold text-gray-900">{formatNumber(stats.summary.total_units_sold)}</p>
            <p className="text-xs text-slate-500">{stats.summary.years_with_sales} ejercicios</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Ingresos Totales</p>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.summary.total_revenue)}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Beneficio Total</p>
            <p className="text-2xl font-bold text-primary-600">{formatCurrency(stats.summary.total_profit)}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Media Anual</p>
            <p className="text-2xl font-bold text-gray-900">{formatNumber(Math.round(stats.summary.avg_units_per_year))}</p>
            <p className="text-xs text-slate-500">unidades/ejercicio</p>
          </div>
        </div>
      )}

      <div className="p-6 space-y-6">
        {/* Current Year Highlight */}
        {(currentYearSales || currentYearPrices) && (
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-3 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Ejercicio {currentYear}
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {currentYearPrices && (
                <>
                  <div>
                    <p className="text-xs text-blue-700">Precio Coste</p>
                    <p className="text-lg font-semibold text-blue-900">{formatCurrency(currentYearPrices.cost_price)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-blue-700">PVP (Tarifa 1)</p>
                    <p className="text-lg font-semibold text-blue-900">{formatCurrency(currentYearPrices.sale_price_1)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-blue-700">Margen</p>
                    <p className="text-lg font-semibold text-blue-900">{formatPercent(currentYearPrices.margin_percent)}</p>
                  </div>
                </>
              )}
              {currentYearSales && (
                <div>
                  <p className="text-xs text-blue-700">Ventas {currentYear}</p>
                  <p className="text-lg font-semibold text-blue-900 flex items-center gap-1">
                    {formatNumber(currentYearSales.total_quantity)} uds
                    {salesChange !== null && (
                      <span className={cn(
                        "text-xs flex items-center",
                        salesChange >= 0 ? "text-green-600" : "text-red-600"
                      )}>
                        {salesChange >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                        {formatPercent(Math.abs(salesChange))}
                      </span>
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Price History Table */}
        {stats.price_history.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-slate-500" />
              Historial de Precios por Ejercicio
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Ejercicio</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">P. Coste</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">PVP T1</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">PVP T2</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">PVP T3</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Margen</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {stats.price_history.map((item) => (
                    <tr key={item.ejercicio} className={item.ejercicio === currentYear ? "bg-blue-50" : "hover:bg-slate-50"}>
                      <td className="px-3 py-2 font-medium text-gray-900">{item.ejercicio}</td>
                      <td className="px-3 py-2 text-right text-slate-600">{formatCurrency(item.cost_price)}</td>
                      <td className="px-3 py-2 text-right text-gray-900 font-medium">{formatCurrency(item.sale_price_1)}</td>
                      <td className="px-3 py-2 text-right text-slate-600">{formatCurrency(item.sale_price_2)}</td>
                      <td className="px-3 py-2 text-right text-slate-600">{formatCurrency(item.sale_price_3)}</td>
                      <td className="px-3 py-2 text-right">
                        <span className={cn(
                          "px-2 py-0.5 rounded text-xs font-medium",
                          item.margin_percent && item.margin_percent >= 30 ? "bg-green-100 text-green-700" :
                          item.margin_percent && item.margin_percent >= 15 ? "bg-yellow-100 text-yellow-700" :
                          "bg-red-100 text-red-700"
                        )}>
                          {formatPercent(item.margin_percent)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Sales History Table */}
        {stats.sales_by_year.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
              <ShoppingCart className="h-4 w-4 text-slate-500" />
              Historial de Ventas por Ejercicio
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Ejercicio</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Cantidad</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Ingresos</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Coste</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Beneficio</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">P. Medio</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Facturas</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {stats.sales_by_year.map((item) => (
                    <tr key={item.ejercicio} className={item.ejercicio === currentYear ? "bg-blue-50" : "hover:bg-slate-50"}>
                      <td className="px-3 py-2 font-medium text-gray-900">{item.ejercicio}</td>
                      <td className="px-3 py-2 text-right text-gray-900 font-medium">{formatNumber(item.total_quantity)}</td>
                      <td className="px-3 py-2 text-right text-green-600 font-medium">{formatCurrency(item.total_revenue)}</td>
                      <td className="px-3 py-2 text-right text-slate-600">{formatCurrency(item.total_cost)}</td>
                      <td className="px-3 py-2 text-right">
                        <span className={cn(
                          "font-medium",
                          item.total_profit >= 0 ? "text-primary-600" : "text-red-600"
                        )}>
                          {formatCurrency(item.total_profit)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right text-slate-600">{formatCurrency(item.avg_sale_price)}</td>
                      <td className="px-3 py-2 text-right text-slate-600">{item.num_invoices}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Sales by Client Type */}
        {stats.sales_by_client_type && stats.sales_by_client_type.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
              <Users className="h-4 w-4 text-slate-500" />
              Ventas por Tipo de Cliente
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {stats.sales_by_client_type.map((item) => {
                const totalQuantity = stats.sales_by_client_type.reduce((sum, i) => sum + i.total_quantity, 0)
                const percentage = totalQuantity > 0 ? (item.total_quantity / totalQuantity) * 100 : 0
                const bgColor = item.client_type === '002' ? 'bg-purple-50 border-purple-200' :
                               item.client_type === '004' ? 'bg-amber-50 border-amber-200' :
                               'bg-emerald-50 border-emerald-200'
                const textColor = item.client_type === '002' ? 'text-purple-700' :
                                 item.client_type === '004' ? 'text-amber-700' :
                                 'text-emerald-700'
                const barColor = item.client_type === '002' ? 'bg-purple-500' :
                                item.client_type === '004' ? 'bg-amber-500' :
                                'bg-emerald-500'
                return (
                  <div key={item.client_type} className={cn("rounded-lg border p-4", bgColor)}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={cn("font-semibold", textColor)}>{item.client_type_name}</span>
                      <span className={cn("text-xs font-medium px-2 py-0.5 rounded-full", bgColor, textColor)}>
                        {percentage.toFixed(1)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                      <div
                        className={cn("h-2 rounded-full transition-all duration-300", barColor)}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <p className="text-slate-500 text-xs">Cantidad</p>
                        <p className={cn("font-semibold", textColor)}>{formatNumber(item.total_quantity)} uds</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Facturas</p>
                        <p className={cn("font-semibold", textColor)}>{item.num_invoices}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Ingresos</p>
                        <p className="font-semibold text-green-600">{formatCurrency(item.total_revenue)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Beneficio</p>
                        <p className={cn("font-semibold", item.total_profit >= 0 ? "text-primary-600" : "text-red-600")}>
                          {formatCurrency(item.total_profit)}
                        </p>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Monthly Sales Chart (simple bar representation) */}
        {stats.sales_monthly.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-slate-500" />
              Ventas Mensuales (ultimos 24 meses)
            </h4>
            <div className="space-y-1">
              {stats.sales_monthly.slice(0, 12).map((item) => {
                const maxQty = Math.max(...stats.sales_monthly.map(m => m.quantity))
                const widthPercent = maxQty > 0 ? (item.quantity / maxQty) * 100 : 0
                return (
                  <div key={item.year_month} className="flex items-center gap-2 text-sm">
                    <span className="w-16 text-slate-500 text-xs">{item.year_month}</span>
                    <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                      <div
                        className="bg-blue-500 h-full rounded-full transition-all duration-300"
                        style={{ width: `${widthPercent}%` }}
                      />
                    </div>
                    <span className="w-20 text-right text-gray-700 text-xs">
                      {item.quantity} uds
                    </span>
                    <span className="w-24 text-right text-slate-500 text-xs">
                      {formatCurrency(item.revenue)}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
